import { Card, Divider, Typography } from '@healthinal/ui';
import { useBlocker } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import {
  useGetScreeningIndividualPreventiveTherapy,
  useUpdateScreeningIndividualPreventiveTherapy,
} from '../../../../api/created/screening.ts';
import { PreventiveTherapyDto } from '../../../../api/generated/contact-tracing-controller/preventive-therapy-controller.ts';
import { LeavePageWarning } from '../../../../components/LeavePageWarning.tsx';
import { useAutoSaveForm } from '../../../../hooks/useAutoSaveForm.ts';
import {
  PreventiveTherapyFormFields,
  getPreventiveTherapyDefaultValues,
  mapPreventiveTherapyFormValuesToPayload,
  preventiveTherapyFieldMetaInfos,
} from '../../../contact-tracing/$patientId/components/preventive-therapy/PreventiveTherapyFormFields.tsx';

export function ScreeningPreventiveTherapyCard({ individualId }: { individualId: string }) {
  const { data: preventiveTherapy, isLoading } = useGetScreeningIndividualPreventiveTherapy(individualId);

  if (isLoading) {
    return (
      <Card>
        <Typography level={'title-lg'}>Präventive Therapie</Typography>
        <Divider />
      </Card>
    );
  }

  if (!preventiveTherapy) {
    return (
      <Card>
        <Typography level={'title-lg'}>Präventive Therapie</Typography>
        <Divider />
        <Typography level={'body-sm'}>Keine TBI Therapie Daten gefunden.</Typography>
      </Card>
    );
  }

  return <ScreeningPreventiveTherapyCardContent individualId={individualId} preventiveTherapy={preventiveTherapy} />;
}

function ScreeningPreventiveTherapyCardContent({
  individualId,
  preventiveTherapy,
}: {
  individualId: string;
  preventiveTherapy: PreventiveTherapyDto;
}) {
  const updatePreventiveTherapyMutation = useUpdateScreeningIndividualPreventiveTherapy();

  const {
    registerWithSaveOnBlur,
    control,
    getFieldState,
    handleSaveOnBlur,
    formState: { errors, isFormDirty },
    scheduleSave,
  } = useAutoSaveForm({
    sourceData: preventiveTherapy,
    getDefaultValues: getPreventiveTherapyDefaultValues,
    save: (data) =>
      updatePreventiveTherapyMutation.mutateAsync({
        id: individualId,
        data: mapPreventiveTherapyFormValuesToPayload(data, {
          id: preventiveTherapy.id,
          patientId: preventiveTherapy.patientId,
        }),
      }),
    fieldMetaInfos: preventiveTherapyFieldMetaInfos,
    saveMode: 'blur',
    validationMode: 'onChange',
    onValidationFailed: () =>
      toast.warn('TBI Therapie kann aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('validationError'),
  });

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    withResolver: true,
  });

  return (
    <Card>
      <Typography level={'title-lg'}>Präventive Therapie</Typography>
      <Divider />
      <PreventiveTherapyFormFields
        registerWithSaveOnBlur={registerWithSaveOnBlur}
        control={control}
        getFieldState={getFieldState}
        handleSaveOnBlur={handleSaveOnBlur}
        errors={errors}
      />
      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onDelete={() => blocker.proceed?.()}
        onSave={() => {
          if (blocker.reset) {
            blocker.reset();
          }
          scheduleSave();
        }}
        title={'TBI Therapie behalten?'}
      />
    </Card>
  );
}
